/**
 * Catalog: products, prices and payment links (Stripe's model).
 *
 * The shop sets every price here; a buy button or payment link only names a
 * link or price, so a buyer can't change what they pay. Prices never change:
 * a new price replaces the old one, which is archived.
 */

import { API_BASE, adminFetch, getInitData, getSessionToken } from "./api";

export interface Price {
  id: string;
  object: "price";
  product: string;
  active: boolean;
  /** null when the customer chooses the amount. */
  unit_amount_usd_cents: number | null;
  custom_unit_amount: {
    minimum_usd_cents: number | null;
    maximum_usd_cents: number | null;
    preset_usd_cents: number | null;
  } | null;
  created_at: number;
}

export interface Product {
  id: string;
  object: "product";
  name: string;
  description: string | null;
  image_url: string | null;
  active: boolean;
  default_price: Price | null;
  created_at: number;
  updated_at: number;
}

/** `{ unit_amount_usd_cents }` for a fixed price, or a customer-chosen range. */
export type PriceData =
  | { unit_amount_usd_cents: number }
  | { custom_unit_amount: { minimum_usd_cents: number; maximum_usd_cents: number; preset_usd_cents?: number | null } };

export interface PaymentLink {
  id: string;
  object: "payment_link";
  url: string;
  active: boolean;
  line_items: Array<{
    price: Price | null;
    product: { id: string; name: string; image_url: string | null; active: boolean } | null;
    quantity: number;
    adjustable_quantity: { enabled: boolean; minimum?: number; maximum?: number };
  }>;
  collect_email: boolean;
  collect_shipping_address: boolean;
  custom_fields: Array<{ key: string; label: { type: "custom"; custom: string }; type: "text"; optional: boolean }>;
  after_completion:
    | { type: "redirect"; redirect: { url: string } }
    | { type: "hosted_confirmation"; hosted_confirmation: { custom_message: string | null } };
  paid_count: number;
  paid_usd_cents: number;
  created_at: number;
  updated_at: number;
}

export interface PaymentLinkInput {
  line_items: Array<{
    price?: string;
    product?: string;
    quantity: number;
    adjustable_quantity?: { enabled: boolean; minimum?: number; maximum?: number };
  }>;
  collect_email?: boolean;
  collect_shipping_address?: boolean;
  custom_fields?: Array<{ key: string; label: string; optional: boolean }>;
  after_completion?: PaymentLink["after_completion"];
  active?: boolean;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const listProducts = () => adminFetch<{ data: Product[] }>("/api/admin/products");

export const createProduct = (p: { name: string; description?: string | null; default_price_data: PriceData }) =>
  adminFetch<Product>("/api/admin/products", jsonInit("POST", p));

/** `default_price_data` adds a new price and archives the old one. */
export const updateProduct = (
  id: string,
  p: { name?: string; description?: string | null; active?: boolean; default_price_data?: PriceData },
) => adminFetch<Product>(`/api/admin/products/${encodeURIComponent(id)}`, jsonInit("POST", p));

/** Multipart upload; adminFetch would set JSON headers, so build the auth here. */
export const uploadProductImage = async (id: string, file: File | null): Promise<Product> => {
  const headers: Record<string, string> = {};
  const session = getSessionToken();
  if (session) headers.Authorization = `Bearer ${session}`;
  else headers["Telegram-Init-Data"] = getInitData();
  let body: FormData | undefined;
  if (file) {
    body = new FormData();
    body.append("file", file);
  }
  const res = await fetch(`${API_BASE.replace(/\/+$/, "")}/api/admin/products/${encodeURIComponent(id)}/image`, {
    method: file ? "POST" : "DELETE",
    headers,
    body,
  });
  const j = (await res.json().catch(() => ({}))) as Product & { error?: { message?: string } };
  if (!res.ok) throw new Error(j.error?.message ?? `Upload failed (${res.status})`);
  return j;
};

export const listPaymentLinks = () => adminFetch<{ data: PaymentLink[] }>("/api/admin/payment_links");

export const createPaymentLink = (l: PaymentLinkInput) =>
  adminFetch<PaymentLink>("/api/admin/payment_links", jsonInit("POST", l));

export const updatePaymentLink = (id: string, l: Partial<PaymentLinkInput>) =>
  adminFetch<PaymentLink>(`/api/admin/payment_links/${encodeURIComponent(id)}`, jsonInit("POST", l));

/** Let the page start a checkout from a cart of price IDs (pk_live_ key). */
export const setClientPrices = (enabled: boolean) =>
  adminFetch<{ enabled: boolean }>("/api/admin/checkout_key/client_prices", jsonInit("PUT", { enabled }));

export const usd = (cents: number): string =>
  "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$49.99", or "$5.00 – $500.00" for a customer-chosen amount. */
export const priceLabel = (p: Price | null): string => {
  if (!p) return "No price";
  if (p.unit_amount_usd_cents != null) return usd(p.unit_amount_usd_cents);
  const c = p.custom_unit_amount;
  return c ? `Customer chooses · ${usd(c.minimum_usd_cents ?? 100)} – ${usd(c.maximum_usd_cents ?? 10_000_000)}` : "Customer chooses";
};

/** Dollars typed by the merchant → cents, or null ("49.99", "$1,299"). */
export const toCents = (s: string): number | null => {
  const t = s.trim().replace(/^\$/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
};

/** Script tag + buy button a shop pastes into its site. */
export const buyButtonSnippet = (origin: string, linkId: string, label = "Buy now"): string =>
  `<script src="${origin}/widget.js" defer></script>\n<button data-shieldz-link="${linkId}">${label}</button>`;
